"use client"

import * as Sentry from "@sentry/nextjs"

const isProduction = process.env.NODE_ENV === "production"

export const isPaidPlan = (plan) => {
	const normalizedPlan = `${plan || ""}`.toLowerCase()

	return Boolean(normalizedPlan && normalizedPlan !== "free" && !normalizedPlan.startsWith("free-"))
}

export const configureSentryUser = ({ email, plan, credits } = {}) => {
	if (!isProduction) return

	if (email) {
		Sentry.setUser({ email })
	}

	if (plan) {
		Sentry.setTag("user_plan", plan)
	}

	if (credits !== undefined && credits !== null) {
		Sentry.setContext("billing", {
			credits: Number(credits)
		})
	}
}

export const startTargetedReplay = (reason, context = {}) => {
	if (!isProduction) return

	try {
		Sentry.setTag("replay_targeted", "true")
		Sentry.setTag("replay_reason", reason)
		Sentry.setContext("replay_targeting", {
			reason,
			...context
		})

		const replay = Sentry.getReplay()
		const isAlreadyRecordingSession =
			replay?.getRecordingMode?.() === "session" && replay?.getReplayId?.()

		if (!isAlreadyRecordingSession) {
			replay?.start()
		}

		return replay
	} catch (error) {
		console.warn("Failed to start targeted Sentry replay:", error)
	}
}

export const markPaymentIntentForReplay = (reason, context = {}) => {
	startTargetedReplay(`payment_intent:${reason}`, context)
}

export const flushReplay = async () => {
	if (!isProduction) return

	try {
		const flushPromise = Sentry.getReplay()?.flush?.()
		await flushPromise
	} catch (error) {
		console.warn("Failed to flush Sentry replay:", error)
	}
}

export const captureReplayWorthyError = (error, reason, context = {}) => {
	if (!isProduction) return

	try {
		Sentry.setTag("replay_targeted", "true")
		Sentry.setTag("replay_reason", `error:${reason}`)
		Sentry.setContext("replay_error_context", {
			reason,
			...context
		})

		Sentry.captureException(error, {
			tags: {
				replay_targeted: "true",
				replay_reason: `error:${reason}`
			},
			extra: context
		})

		const flushPromise = Sentry.getReplay()?.flush?.()
		flushPromise?.catch((flushError) => {
			console.warn("Failed to flush Sentry replay after error:", flushError)
		})
	} catch (captureError) {
		console.warn("Failed to capture replay-worthy error:", captureError)
	}
}
